import { Component, OnInit } from '@angular/core';
import { EventQuestion, EventType, ScheduledEventsColumn } from '../../../../../../models/event-type';
import { ActivatedRoute } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import { EventTypeService } from '../../../../../../services/event-type/event-type.service';
import { AccountService } from '../../../../../../services/account/account.service';
import { CustomCustomerField } from '../../../../../../models/organization-config';
import { OrganizationConfigService } from '../../../../../../services/organization-config/organization-config.service';
import { getScheduledEventsColumnHeaderName } from '../../../../../../utils-models';
import {
  AddScheduledEventsColumnModalComponent
} from './modals/add-scheduled-events-column-modal/add-scheduled-events-column-modal.component';
import { EventQuestionScope, ScheduledEventsColumnType, ScheduleType } from '../../../../../../models/event-type-enums';


@Component({
  selector: 'app-event-type-scheduled-events-columns',
  templateUrl: './event-type-scheduled-events-columns.component.html',
  styleUrls: ['./event-type-scheduled-events-columns.component.css']
})
export class EventTypeScheduledEventsColumnsComponent implements OnInit {
  public eventTypeIdInt = 0;
  public eventType: EventType = null;

  public participantQuestions: EventQuestion[] = null;
  public customCustomerFields: CustomCustomerField[] = null;

  public columnsChangedSubject = new Subject<null>();

  public loadingEventType = true;
  public loadingEventQuestionsList = true;
  public loadingCustomCustomerFields = true;
  public loadingSave = false;
  public saveRequestCount = 0;

  public successMessage = '';
  public errorMessage = '';
  public eventTypeNotFound = false;
  public serverError: number = null;

  public readonly getScheduledEventsColumnHeaderName = getScheduledEventsColumnHeaderName;
  public readonly ScheduleType = ScheduleType;

  constructor(
    private route: ActivatedRoute,
    private accountService: AccountService,
    private eventTypeService: EventTypeService,
    private orgConfigService: OrganizationConfigService,
    private modalService: NgbModal
  ) {}

  ngOnInit() {
    const routeParams = this.route.snapshot.params;
    this.eventTypeIdInt = parseInt(routeParams.id, 10);
    if (isNaN(this.eventTypeIdInt)) {
      this.eventTypeNotFound = true;
      return;
    }

    this.columnsChangedSubject.pipe(
      debounceTime(500)
    ).subscribe(() => {
      this.saveColumns();
    });

    this.loadEventType(this.eventTypeIdInt);
    this.loadEventQuestions(this.eventTypeIdInt);
    this.loadCustomCustomerFields();
  }

  public getColumnTypeName(column: ScheduledEventsColumn) {
    switch (column.column_type) {
      case ScheduledEventsColumnType.capitanProfileField:
        return 'Capitan Profile Field';
      case ScheduledEventsColumnType.customCustomerField:
        return 'Custom Customer Field';
      case ScheduledEventsColumnType.attendeeQuestion:
        return 'Attendee Question';
      default:
        return 'Reservation Field';
    }
  }

  public shiftColumnByOne(columnIdx: number, shiftUp: boolean) {
    const columns = this.eventType.scheduled_events_columns;
    const column = columns[columnIdx];

    if (shiftUp && (columnIdx === 0)) { return; }
    if (!shiftUp && (columnIdx >= (columns.length - 1))) { return; }

    if (shiftUp) {
      columns[columnIdx] = columns[columnIdx - 1];
      columns[columnIdx - 1] = column;
    } else {
      columns[columnIdx] = columns[columnIdx + 1];
      columns[columnIdx + 1] = column;
    }

    this.columnsChangedSubject.next();
  }

  public shiftColumnToExtreme(columnIdx: number, shiftUp: boolean) {
    const columns = this.eventType.scheduled_events_columns;
    const column = columns[columnIdx];

    if (shiftUp && (columnIdx === 0)) { return; }
    if (!shiftUp && (columnIdx >= (columns.length - 1))) { return; }

    columns.splice(columnIdx, 1);
    if (shiftUp) {
      columns.unshift(column);
    } else {
      columns.push(column);
    }

    this.columnsChangedSubject.next();
  }

  public removeColumn(columnIdx: number) {
    this.eventType.scheduled_events_columns.splice(columnIdx, 1);
    this.columnsChangedSubject.next();
  }

  public openAddColumnModal() {
    this.errorMessage = '';
    this.successMessage = '';

    const modalRef = this.modalService.open(AddScheduledEventsColumnModalComponent);
    modalRef.componentInstance.eventType = this.eventType;
    modalRef.componentInstance.eventQuestions = this.participantQuestions;
    modalRef.componentInstance.customCustomerFields = this.customCustomerFields;
    modalRef.componentInstance.savedCallback = (column: ScheduledEventsColumn) => {
      this.eventType.scheduled_events_columns.push(column);
      this.columnsChangedSubject.next();
    };
  }

  public loadingInit() {
    return (
      this.loadingEventType ||
      this.loadingEventQuestionsList ||
      this.loadingCustomCustomerFields
    );
  }

  /**
   * Load the event type with the given ID.
   *
   * @param eventTypeId - The API ID of the event type.
   */
  private loadEventType(eventTypeId: number) {
    this.loadingEventType = true;

    this.eventTypeService
      .getEventType(eventTypeId)
      .then(eventType => {
        this.eventType = eventType;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else if (reason.status === 404) {
          this.eventTypeNotFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingEventType = false; });
  }

  /**
   * Load the event questions with the given event type ID.
   *
   * @param eventTypeId - The API ID of the event type.
   */
  private loadEventQuestions(eventTypeId: number) {
    this.loadingEventQuestionsList = true;

    this.eventTypeService
      .getEventQuestions(eventTypeId, 1, 100)
      .then(questionResponse => {
        this.participantQuestions = questionResponse.results;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else if (reason.status === 404) {
          this.eventTypeNotFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingEventQuestionsList = false; });
  }

  private loadCustomCustomerFields() {
    this.loadingCustomCustomerFields = true;

    this.orgConfigService
      .getCustomCustomerFields(1, 3000)
      .then(result => { this.customCustomerFields = result.results; })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingCustomCustomerFields = false; });
  }

  private saveColumns() {
    if (!this.eventType) { return; }

    this.loadingSave = true;

    this.errorMessage = '';
    this.successMessage = '';

    this.saveRequestCount++;
    const thisRequestCount = this.saveRequestCount;

    this.eventTypeService
      .updateEventTypeScheduledEventsColumns(this.eventType)
      .then(response => {
        // Only set the success message if there has not been a more recent
        // save request initiated.
        if (thisRequestCount === this.saveRequestCount) {
          this.successMessage = 'Columns saved.';
        }
      })
      .catch(reason => {
        if (reason.status === 400) {
          this.errorMessage = reason.error.scheduled_events_columns;
          if (!this.errorMessage) {
            this.errorMessage = 'Unknown error.';
          }
        } else if (reason.status === 403) {
          this.errorMessage = 'You do not have permission to modify this event.';
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => {
        if (thisRequestCount === this.saveRequestCount) {
          this.loadingSave = false;
        }
      });
  }
}
